import axios from "axios";
import { setCookie, getCookie } from "cookies-next";
import JSZip from "jszip";
import log from "../lib/log";

const ACCESS_TOKEN_START = "#access_token=";
const MAXIMIUM_LOGS_IN_QUEUE = 100000;
const logQueue = [];

/**
 * Creates Async Axios Post function to Middleware.  Throws if an error occurs
 * 
 * @param {*} path Path on mw of endpoint
 * @param {*} body Body of post
 * @param {*} options Options for post
 * @returns Middleware respone ro throws on error
 */
const post = async (path, body = null, options = null) => {
  return await axios.post(
    process.env.NEXT_PUBLIC_REACT_APP_DB_URL + path,
    body,
    options
  );
};
/**
 * Sets up and refreshes middleware token.  First call just sets up ongoing refresh
 *
 */
const refreshToken = async (failed) => {
  let refreshTime = 1000 * 60 * 12; // Refresh every 12 minutes
  const oldToken = getCookie("ROMTechAT", { path: '/' }); // RS-26602 Set cookies to be available on all pages
  if (!oldToken) { 
    console.error("No current token to refresh");
    log("No current token to refresh");
  }
  else {
    try {
      const resp = await axios.post("/api/v1/refresh", {
        headers: {
          'Authorization': `Bearer ${oldToken}`,
        }
      });
      console.log("Refresh Token " + resp.data.token);
      if (resp.status === 200) {
        setCookie("ROMTechAT", resp.data.token, { path: '/' });
      } else {
        console.log("Could not refresh token")
        log(`Could not refresh token: ${resp.data}`)
      }
    } catch (error) {
      console.log("Error refreshing token: ", error);
      log(`Error refreshing token: ${JSON.stringify(error)}`);
      // RS-26602 Grab error message from response if available
      let responseData = error.response.data
      var message = error.response.status
      if (responseData) {
          if (responseData.error) {
              message = responseData.error
          }
      }
      failed({
        code: error.response.status,
        message: message
      })
    }
    setTimeout(() => {
      refreshToken();
    }, refreshTime);
  }
};

/**
 * Gets Okta Access token from URL of current page after Okta redirections
 *
 * @returns Access token or null if none
 */
const getOktaAccessToken = async () => {
  const urlComponents = window.location.href.split("&");
  var token = urlComponents[0];
  if (token.includes(ACCESS_TOKEN_START)) {
    var tokenindex = token.indexOf(ACCESS_TOKEN_START);
    token = token.slice(tokenindex + ACCESS_TOKEN_START.length);
    var state = urlComponents[4];
    state = state.replace("state=", "");
    state = state.replace("%2B", "+");
    state = state.replace("%40", "@");

    const values = {
      state: state,
      accessToken: token,
    };
    return values;
  }
  return null;
};

/**
 * Grabs needed data from response after login
 * 
 * @param {*} res Response from Middleware login
 * @returns Multiple values
 */
const makeUserStoreAfterLogin = async (res) => {
  const { access_token, user, middleware_version, mfg_api, maintenance } = res;

  const practice =
    user && user.practice_team_member && user.practice_team_member.practice;

  return {
    token: access_token,
    // RTS-693-
    mfgAPI: mfg_api,
    middlewareVersion: middleware_version,
    user: {
      ...user,
    },
    practice,
    maintenance,
  };
};

/**
 * Download a file from a given URL
 * @param {*} url the URL of the file to download
 * @param {*} name the name of the file to download
 */
const download = (url, name) => {
	const a = document.createElement('a')
	a.href = url
	a.download = name
	a.click()
}

/**
 * Convert date-time to file string format
 * @param {*} now the current date-time
 * @returns a formatted date-time string
 */
const dateTimeToFileString = (now) => {
  const date = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`
  const time = `${now.getHours()}-${("0" + now.getMinutes()).slice(-2)}-${("0" + now.getSeconds()).slice(-2)}-${("00" + now.getMilliseconds()).slice(-3)}`
	const randomChars = Array.from({length:6}, () => String.fromCharCode(65 + Math.floor(Math.random() * 26))).join('')
  const dateTimeStr = `${date}_${time}_${randomChars}`
  return dateTimeStr
}

/**
 * Convert date-time to log string format
 * @param {*} now the current date-time
 * @returns a formatted date-time string
 */
const dateTimeToLogString = (now) => {
  const date = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`
  const time = `${now.getHours()}:${("0" + now.getMinutes()).slice(-2)}:${("0" + now.getSeconds()).slice(-2)}.${("00" + now.getMilliseconds()).slice(-3)}`
  const dateTimeStr = `${date}-${time}`
  return dateTimeStr
}

/**
 * Takes in multiple files & zips them into a single folder
 * @param {*} files the files to zip
 * @param {*} name the name of the zip file
 * @returns the zipped file
 */
const zipFiles = async (files, name) => {
	const zip = new JSZip()
	files.forEach((data) => {
		zip.file(data.name, data)
	})

	const content = await zip.generateAsync({ type: "blob" })
	const file = new File([content], name, { type: "application/zip" })
	return file
}

/**
 * Write a log message to the log queue. If the queue is full, remove the oldest log message.
 * @param {*} args the log message to write to the queue
 */
const logToQueue = (args) => {
	logQueue.push(args)
	if (logQueue.length > MAXIMIUM_LOGS_IN_QUEUE) {
		logQueue.shift()
	}
}

/**
 * Get the log queue
 * @returns the log queue
 */
const getLogQueue = () => {
	return logQueue
}

export const utils = {
  post,
  getOktaAccessToken,
  makeUserStoreAfterLogin,
  refreshToken,
  dateTimeToFileString,
	dateTimeToLogString,
	logToQueue,
	getLogQueue,
	zipFiles,
	download
};
